"use client";

import { ReactNode } from "react";
import { SplitFactoryProvider } from "@splitsoftware/splitio-react";

import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/next";

import { IdentityProvider } from "@zapier/identity";

import AuthedPage from "./AuthedPage";
import { AccountIdMonitor } from "./AccountIdMonitor";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  FullStoryWithSession,
  TrackRouting,
  initializeDatadogRum,
  initializeSentry,
  nextJsReportWebVitals,
} from "../observability";

import { isDatadog } from "@utils/isDatadog";

interface Props {
  children: ReactNode;
}

const shouldInitSentry = !isDatadog();
if (shouldInitSentry) {
  initializeSentry({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN || "",
    commitHash: process.env.NEXT_PUBLIC_COMMIT_SHA || "",
    environment: process.env.NEXT_PUBLIC_VERCEL_ENV || "",
  });
}

// Configure Split to accessing feature toggles client-side in the browser.
const sdkConfig: SplitIO.IBrowserSettings = {
  core: {
    authorizationKey: process.env.SPLIT_CLIENT_API_KEY || "localhost",
    key: "user_id",
  },
  features: {
    SayHello: "on",
  },
};

// Measure and report web vitals that Next.js tracks to Datadog.
// See: https://nextjs.org/docs/advanced-features/measuring-performance
initializeDatadogRum();
export const reportWebVitals = nextJsReportWebVitals;

const queryClient = new QueryClient();

export default function AppLayout({ children }: Props) {
  return (
    <IdentityProvider>
      <TrackRouting>
        <SplitFactoryProvider config={sdkConfig}>
          <QueryClientProvider client={queryClient}>
            <AuthedPage>
              <AccountIdMonitor />
              {children}
              <Analytics />
              <SpeedInsights />
              <FullStoryWithSession />
            </AuthedPage>
          </QueryClientProvider>
        </SplitFactoryProvider>
      </TrackRouting>
    </IdentityProvider>
  );
}
